import React, { useState, useMemo, useContext } from 'react'
import PageLoader from 'components/PageLoader'
import PageHeader from 'components/PageHeader'
import request from 'services/http'
import { tap } from 'rxjs/operators'
import { useSubscription } from 'utils'
import { Icon, Button, Header, Segment } from 'semantic-ui-react'
import UploadCertificateForm from './UploadCertificateForm'
import DownloadCertificateForm from './DownloadCertificateForm'
import Table from 'components/Table'
import { cpfCnpj } from '@rededompedro/utils'
import { DateTime } from 'luxon'
import { GlobalContext } from 'globalContext'

export default function Empresas() {

    const [global] = useContext(GlobalContext)
    const [empresas, setEmpresas] = useState(null)
    const [acao, setAcao] = useState('lista')

    const dados$ = useMemo(() => request({ 
        url: '/empresas',
        method: 'GET' 
    }).pipe(
        tap( ({ response }) => setEmpresas(response.empresas))
    ), [setEmpresas])

    const config = {
        columns: [
            { field: 'codigo', text: 'Código', sort: true },
            { field: 'nomeEmpresa', text: 'Empresa', sort: true },
            { field: 'estado', text: 'Estado', sort: true },
            { field: 'cnpj 1', text: 'Cnpj 1', sort: true },
            { field: 'certificado 1', text: 'Certificado 1', sort: true },
            { field: 'cnpj 2', text: 'Cnpj 2', sort: true },
            { field: 'certificado 2', text: 'Certificado 2', sort: true },
            { field: 'status', text: 'Status', sort: false }
        ],
        data: empresas ? empresas.map(x => {
            let status = ''
            if (x.certificados.filter(y => y.status === 'warning').length)
                status = <Button size='mini' content={<><Icon name='exclamation triangle' color='yellow' /> Expirando em breve</>}  color='black' fluid />
            if (x.certificados.length === 0 || x.certificados.filter(y => y.status === 'expired').length)
                status = <Button size='mini' content={<><Icon name='times circle' color='red' /> Expirado</>} color='black' fluid />
            if (status === '')
                status = <Button size='mini' content={<><Icon name='check circle' color='green' /> Ok</>}  color='black' fluid />

            return Object.assign({}, {
                "codigo": x.codDpnet,
                "id": x._id,
                "nomeEmpresa": x.nomeEmpresa,
                "estado": x.estado,
                "cnpj 1": x.certificados.length > 0 ? cpfCnpj(x.certificados.find(y => y && y.cnpj === x.cnpjPrincipal).cnpj) : cpfCnpj(x.cnpjPrincipal),
                "certificado 1": x.certificados.length > 0 ? DateTime.fromMillis(x.certificados.find(y => y && y.cnpj === x.cnpjPrincipal).validity.end).toFormat('dd/MM/yyyy HH:mm:ss') : 'Não configurado',
                "cnpj 2": x.certificados.length > 1 ? cpfCnpj(x.certificados.find(y => y && y.cnpj !== x.cnpjPrincipal).cnpj) : 'Não configurado',
                "certificado 2": x.certificados.length > 1 ? DateTime.fromMillis(x.certificados.find(y => y && y.cnpj !== x.cnpjPrincipal).validity.end).toFormat('dd/MM/yyyy HH:mm:ss') : 'Não configurado',
                "status": status
            })
        }) : [],
        filter: true
    }

    useSubscription(dados$, global.empresas && empresas === null)

    return (
        <PageLoader active={empresas === null}>
            <div id="empresas" className='page-content'>

                <PageHeader />

                {acao !== 'lista' && <Segment className='subcontent'>
                    <div className="row flex-grow-1">
                        <div className="col d-flex flex-column">
                            <div className="row mx-0">
                                <div className="col">
                                    <Header as='h3'>
                                        <Icon.Group size='big'>
                                            <Icon name='tasks' className='primary-color' />
                                        </Icon.Group>
                                        <Header.Content className='primary-color'>
                                            Ações 
                                            <Header.Subheader className='secondary-color'><strong>Escolha a ação desejada</strong></Header.Subheader>
                                        </Header.Content>
                                    </Header>
                                </div>
                            </div>
                            <div className="row flex-grow-1 flex-column">
                                <div className="col d-flex align-items-center flex-column justify-content-center">
                                    <Button icon className={acao !== '' ? 'w-50 btn-primary mt-2' : 'w-25 btn-primary mt-2'} color='blue' onClick={() => setAcao('lista')} size='small' >
                                        <Icon name='list alternate outline' /> Lista Empresas
                                    </Button>
                                    <Button icon className={acao !== '' ? 'w-50 btn-primary mt-2' : 'w-25 btn-primary mt-2'} color='blue' onClick={() => setAcao('configurar')} size='small' >
                                        <Icon name='wrench' /> Configurar Certificado
                                    </Button>
                                    <Button icon className={acao !== '' ? 'w-50 btn-primary mt-2' : 'w-25 btn-primary mt-2'} color='blue' onClick={() => setAcao('download')} size='small' >
                                        <Icon name='download' /> Download Certificado
                                    </Button>
                                </div>
                            </div>
                        </div>
                        {acao === 'configurar' && <div className="col d-flex flex-column flex-grow-1">
                            <Header as='h3'>
                                <Icon.Group size='big'>
                                    <Icon name='clipboard list' className='primary-color' />    
                                </Icon.Group>
                                <Header.Content className='primary-color'>
                                    Configurar Certificado
                                    <Header.Subheader className='secondary-color'>
                                        <strong>Adicionar/Trocar certificados digitais</strong>
                                    </Header.Subheader>
                                </Header.Content>
                            </Header>
                            <div className='d-flex flex-column flex-grow-1 align-items-center justify-content-center'>
                                <UploadCertificateForm />
                            </div>
                        </div>}
                        {acao === 'download' && <div className="col d-flex flex-column flex-grow-1">
                            <Header as='h3'>
                                <Icon.Group size='big'>
                                    <Icon name='clipboard list' className='primary-color' />    
                                </Icon.Group>
                                <Header.Content className='primary-color'>
                                    Download de Certificado
                                    <Header.Subheader className='secondary-color'>
                                        <strong>Baixar certificados digitais</strong>
                                    </Header.Subheader>
                                </Header.Content>
                            </Header>
                            <div className='d-flex flex-column flex-grow-1 align-items-center justify-content-center'>
                                <DownloadCertificateForm empresas={empresas} />
                            </div>
                        </div>}
                    </div>
                </Segment>}

                {acao === 'lista' && <Segment className='subcontent' >
                    <div className="row mb-3">
                        <div className="col">
                            <Button color='blue' size='mini' floated='right' onClick={() => setAcao('')}>Opções</Button>
                        </div>
                    </div>
                    <div className="row flex-grow-1">
                        <div className="col">
                            <Table id='table-empresas' singleLine inverted sortable selectable {...config} />
                        </div>
                    </div>
                </Segment>}
                
            </div>
        </PageLoader>
    )
}
